import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import { formatMoney } from '../../lib/money';
import type { BusinessAISettings } from '../../types';
import type { AIUsageSummary } from '../../hooks/useAISettings';

interface Props {
  settings: BusinessAISettings | null;
  usage: AIUsageSummary;
  loading: boolean;
  onToggle: (field: string, value: boolean) => void;
}

const FEATURES: { key: keyof BusinessAISettings; label: string; desc: string }[] = [
  { key: 'description_cleanup', label: 'Description cleanup', desc: 'Auto-improve job and project descriptions' },
  { key: 'message_suggestions', label: 'Message suggestions', desc: 'Smart reply suggestions in messaging' },
  { key: 'quote_scope_writer', label: 'Quote scope writer', desc: 'AI-assisted scope of work for quotes' },
  { key: 'receipt_scanning', label: 'Receipt scanning', desc: 'AI categorization of scanned receipts' },
  { key: 'expense_categorization', label: 'Expense categorization', desc: 'Auto-categorize expenses by vendor' },
  { key: 'daily_summary', label: 'Daily summary', desc: 'AI-generated end-of-day business summary' },
];

const ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${palette.g100}` };

function Toggle({ on, disabled, onChange }: { on: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} disabled={disabled} style={{
      width: 40, height: 22, borderRadius: 11, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: on ? palette.accent : palette.g300, position: 'relative', transition: 'background 0.2s',
      opacity: disabled ? 0.5 : 1,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 9, background: palette.white, position: 'absolute', top: 2,
        left: on ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      }} />
    </button>
  );
}

export function AISettingsSection({ settings, usage, loading, onToggle }: Props) {
  if (loading || !settings) return null;

  const masterOn = settings.ai_enabled;

  return (
    <Card title="AI Features">
      {/* Master toggle */}
      <div style={{ ...ROW, borderBottom: `2px solid ${palette.g150}`, paddingBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>AI Enabled</div>
          <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>Master toggle — disables all AI features when off</div>
        </div>
        <Toggle on={masterOn} onChange={() => onToggle('ai_enabled', !masterOn)} />
      </div>

      {/* Per-feature toggles */}
      {FEATURES.map(f => {
        const val = settings[f.key] as boolean;
        return (
          <div key={f.key} style={{ ...ROW, opacity: masterOn ? 1 : 0.5 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: palette.g700 }}>{f.label}</div>
              <div style={{ fontSize: 11.5, color: palette.g400, marginTop: 1 }}>{f.desc}</div>
            </div>
            <Toggle on={val} disabled={!masterOn} onChange={() => onToggle(f.key, !val)} />
          </div>
        );
      })}

      {/* Usage stats */}
      <div style={{ marginTop: 16, padding: '12px 14px', background: palette.g50, borderRadius: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8 }}>Usage This Month</div>
        <div style={{ display: 'flex', gap: 24, fontSize: 12 }}>
          <div><span style={{ color: palette.g400 }}>Total calls</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{usage.totalCalls}</div></div>
          <div><span style={{ color: palette.g400 }}>Est. cost</span><div style={{ fontWeight: 600, color: palette.g800, marginTop: 2 }}>{formatMoney(usage.estimatedCost)}</div></div>
        </div>
        {usage.byFeature.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {usage.byFeature.map(f => (
              <span key={f.feature} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500, background: palette.accentWash, color: palette.accentDeep }}>
                {f.feature.replace(/_/g, ' ')}: {f.count}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: palette.g400, fontStyle: 'italic' }}>
        AI features use no provider branding. Your data is processed and immediately discarded.
      </div>
    </Card>
  );
}
